import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useLanguage } from '../../hooks/useLanguage';
import { Product } from '../../types/api';
import { formatDate } from '../../utils/dateFormatter';

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const category = typeof product.categoryId === 'object' ? product.categoryId : null;
  const categoryName = category 
    ? (currentLanguage === 'ar' ? category.name : category.nameEn)
    : '';

  return (
    <Link
      to={`/catalog/product/${product._id}`}
      className="block bg-neutral-dark/50 backdrop-blur-sm border border-neutral-dark rounded-lg p-4 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
    >
      <div className="flex flex-col h-full">
        {/* Product Name */}
        <h3 className="text-lg font-heading font-semibold text-neutral-light mb-2 group-hover:text-brand-accent transition-colors">
          {product.name}
        </h3>

        {/* Description */}
        {product.description && (
          <p className="text-sm text-neutral-medium font-body mb-3 flex-1 line-clamp-2 whitespace-pre-line">
            {product.description}
          </p>
        )}

        {/* Category */}
        {categoryName && (
          <div className="mb-3">
            <span className="text-xs text-neutral-medium font-body">
              {t('catalog.product.category')}:
            </span>
            <span className="text-xs text-brand-accent font-body ml-1">
              {categoryName}
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-neutral-dark">
          <div className="flex flex-col">
            <span className="text-xs text-neutral-medium font-body">
              {t('catalog.product.price')}
            </span>
            {category && typeof category.price === 'number' && (
              <span className="text-base font-heading font-bold text-brand-accent">
                {category.price.toFixed(2)} USD
              </span>
            )}
          </div>

          <div className="flex flex-col items-end">
            <span className="text-xs text-neutral-medium font-body">
              {t('catalog.product.added')}
            </span>
            <span className="text-xs text-neutral-light font-body">
              {formatDate(product.createdAt, 'PP h:mm a', currentLanguage)}
            </span>
          </div>
        </div>

        {/* Status Badge */}
        <div className="mt-3">
          <span className={`inline-block px-2 py-1 rounded-full text-xs font-body ${
            product.status === 'available'
              ? 'bg-green-500/20 text-green-500'
              : 'bg-neutral-dark text-neutral-medium'
          }`}>
            {product.status === 'available' 
              ? t('catalog.product.available') 
              : t('catalog.product.sold')}
          </span>
        </div>
      </div>
    </Link>
  );
}

